import includes from 'lodash/includes'
import { useMemo } from 'react'
import { useAllLocalStorage } from '../useStorage/useAllLocalStorage'
import { useHistory } from '../useHistory'
import { requiredCheck } from '@ztechapps/common.utils'
import { useMutation, useQuery } from '@apollo/client/react'
import { logoutQuery, meQuery } from './graphql'
import type { User } from '@ztechapps/common.contract'
import { UserStatusEnum } from '@ztechapps/common.contract'

export type UseUserProps = {
  userIdKey?: string
  tokenKey?: string
}

type GetUserStatusResult = {
  loggedIn: boolean
  hasTenant: boolean
  isPending: boolean
  isActive: boolean
  isInActive: boolean
  isSuperAdmin: boolean
}

const getUserStatus = (user: User): GetUserStatusResult => {
  const { tenantId, status, roles = [], _id } = user || {}
  const hasTenant = !!tenantId
  const isPending = status === UserStatusEnum.pending
  const isActive = status === UserStatusEnum.active
  const isInActive = status === UserStatusEnum.inActive
  const loggedIn = !!_id
  const isSuperAdmin = includes(roles, 'superAdmin')
  return {
    loggedIn,
    hasTenant,
    isPending,
    isActive,
    isInActive,
    isSuperAdmin,
  }
}

const useUserRaw = ({ _id }: { _id?: string }) => {
  const { data, loading, error, refetch } = useQuery<{ me: User }>(meQuery, {
    skip: !_id,
    fetchPolicy: 'cache-first',
  })

  //the signed-in user is whoever the server resolved for this session's token —
  //accept it only when it matches the stored id, so a stale token never surfaces a stranger
  const user = data?.me?._id === _id ? data?.me : undefined

  const status = getUserStatus(user)

  const [mutate] = useMutation(logoutQuery)

  const logout = ({ accessToken }) => {
    requiredCheck(accessToken, 'logout.accessToken')

    return mutate({
      variables: { accessToken },
    })
  }

  return {
    user,
    loading,
    error,
    refetch,
    ...status,
    logout,
    getUserStatus,
  }
}

export type UseUserResult = {
  accessToken: string
  logout: (redirectTo?: string | boolean) => void
  getUserStatus: ({ user }: any) => GetUserStatusResult
  loggedIn: boolean
  hasTenant: boolean
  isPending: boolean
  isActive: boolean
  isInActive: boolean
  isSuperAdmin: boolean
  user: {
    _id: string
    tenantId: string
    timeZone: string
    roles: string[]
    status: string
    profile: {
      name: string
      email: string
      picture: string
    }
  }
  loading: boolean
  refetch: () => void
}

/**
 * This is the useUser for web components, that has access to token and userKey
 * It just wraps the useUser for web simplicity
 */
export const useUser = ({
  userIdKey = 'app-login-userId',
  tokenKey = 'app-login-token',
}: UseUserProps = {}): UseUserResult => {
  const history = useHistory()
  const localStorage = useAllLocalStorage()
  const _id: string = useMemo(() => localStorage.get(userIdKey), [userIdKey, localStorage])
  const accessToken = useMemo(() => localStorage.get(tokenKey), [tokenKey, localStorage])

  const userResult = useUserRaw({ _id })

  const logout = async (redirectTo) => {
    try {
      //a signed-out browser has no token and nothing to revoke on the server — skip the call
      if (accessToken) await userResult?.logout({ accessToken })
    } finally {
      //the session ends here whatever the server answered — a failed revoke (api 5xx) must not leave the
      //visitor signed in on a blank page, so the storage clear and the redirect run on both paths
      localStorage.remove(tokenKey)
      localStorage.remove(userIdKey)

      //redirect to the provided URL; false stays put
      if (redirectTo) history.push(redirectTo)
      //replace the default route: a push would let Back return to the guarded page, whose logout pushes again
      else if (redirectTo !== false) history.push('/account/login', { replace: true })
    }
  }

  return { ...userResult, accessToken, logout }
}
